import { useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

import { ReactComponent as PencilIcon } from "assets/svgs/Editor/pencil.svg";

interface EditableARCNameProps {
    serialNumber: string;
    name?: string;
    onRename: (name: string) => Promise<void>;
    className?: string;
    nameClassName?: string;
}

/**
 * ARC name that edits in place. The input carries the same typography as the
 * read-only label, so only the darker background shows on hover and while
 * editing. Enter or blur saves, Escape reverts. A blank name resets to the serial.
 */
const EditableARCName = ({
    serialNumber,
    name,
    onRename,
    className = "",
    nameClassName = "",
}: EditableARCNameProps) => {
    const displayName = name || serialNumber;
    const [value, setValue] = useState(displayName);
    const [editing, setEditing] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!editing) setValue(displayName);
    }, [displayName, editing]);

    const commit = async () => {
        setEditing(false);
        const next = value.trim() || serialNumber;
        if (next === displayName) {
            setValue(displayName);
            return;
        }
        try {
            await onRename(next);
        } catch {
            setValue(displayName);
        }
    };

    return (
        // Keep clicks inside the editor from triggering the parent card's onClick.
        <div className={twMerge("flex min-w-0 flex-col", className)} onClick={(e) => e.stopPropagation()}>
            <div className="flex min-w-0 items-center">
                <input
                    ref={inputRef}
                    value={value}
                    size={Math.max(value.length, 1)}
                    aria-label="ARC name"
                    title={displayName}
                    onChange={(e) => setValue(e.target.value)}
                    onFocus={() => setEditing(true)}
                    onBlur={commit}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") e.currentTarget.blur();
                        if (e.key === "Escape") {
                            setValue(displayName);
                            setEditing(false);
                            // Blur after the reset so commit() sees the original value.
                            requestAnimationFrame(() => inputRef.current?.blur());
                        }
                    }}
                    style={{ fieldSizing: "content" } as React.CSSProperties}
                    className={twMerge(
                        "min-w-0 max-w-full cursor-text truncate rounded-r5 border-none bg-transparent px-2 py-1 -ml-2 -my-1 text-white outline-none",
                        // -ml-2/-my-1 offset the padding so the text stays where the read-only label sat;
                        // the right padding is the only gap before the pencil.
                        // Card background is black80, so the hover/editing state must be darker than that.
                        "hover:bg-black100 focus:bg-black100",
                        nameClassName
                    )}
                />
                <button
                    type="button"
                    aria-label="Edit ARC name"
                    // Flex + block icon centers the pencil on the text line (an inline SVG
                    // sits on the baseline); pb-1 nudges it up onto the text's optical center.
                    className="flex shrink-0 items-center pb-1 text-black40 hover:text-white"
                    onClick={() => inputRef.current?.focus()}
                >
                    <PencilIcon className="block !h-4 !w-4" />
                </button>
            </div>
            {displayName !== serialNumber && (
                <span className="truncate text-h5 leading-tight text-black40" title={serialNumber}>
                    {serialNumber}
                </span>
            )}
        </div>
    );
};

export default EditableARCName;
